import { isLoggedIn } from "@shapr3d/frontend-lib";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { SyncRequestClient, UMSrequestClient } from "../stores/requestClient";

export type Project = {
  projectId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};
export type S3File = {
  method: string;
  url: string;
  headers?: Record<string, string>;
  expiry: string;
};
export type ProjectRevision = {
  revisionId: number;
  parentRevisionId?: number;
  uploadedByUserId?: string;
  uploadedByDeviceId: string;
  uploadedByPlatform: "ios" | "macos" | "visionos" | "windows";
  createdAt: string;
  uploadedAt?: string;
  lastModifiedAt: string;
  fileSize: number;
  appVersion: string;
  projectVersion: number;
  copyOfRevisionId?: number;
  fileDownload?: S3File;
  thumbnailDownloads?: {
    light?: S3File;
    dark?: S3File;
  };
};

export type ProjectPageViewLink = {
  projectViewLinkId: string;
  webExportId: string;
  createdAt: Date;
  updatedAt: Date;
  published: boolean;
  uploadedByUserId?: string;
};

export type User = {
  userId: string;
  name?: string;
  email: string;
};

export type Space = { spaceName?: string; spaceType: string };

export enum ProjectUserRole {
  Owner = "owner",
  Viewer = "viewer",
  Commenter = "commenter",
}

export type ProjectData = {
  project: Project;
  latestRevision: ProjectRevision;
  projectViewLinks?: ProjectPageViewLink[];
  users: User[];
  space?: Space;
  teamId: string;
  userRole: ProjectUserRole;
};

export const projectQueryKey = ["projectQueryKey"];
export const activeSubscriptionQueryKey = ["activeSubscriptionQueryKey"];

export const useProject = (isInviteAcceptancePending: boolean = false) => {
  const { projectId } = useParams();
  const query = useQuery({
    queryKey: [...projectQueryKey, projectId],
    queryFn: async () => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "GET",
        url: `/web/projects/${projectId}`,
      });
      return response.json<ProjectData>();
    },
    refetchInterval: 1000 * 60 * 60, // 1 hour
    enabled: isLoggedIn() && !isInviteAcceptancePending && Boolean(projectId),
  });

  // Temporary fix to hide the button while sync does not have subscription data from team
  const isDownloadFileEnabledQuery = useQuery({
    queryKey: [...activeSubscriptionQueryKey, query.data?.teamId],
    queryFn: async () => {
      try {
        await UMSrequestClient.publicRequest({
          method: "GET",
          url: `/teams/${query.data?.teamId}/active-subscription`,
        });
        return true;
      } catch {
        return false;
      }
    },
    enabled: Boolean(query.data?.teamId),
  });

  return { ...query, isDownloadFileEnabled: isDownloadFileEnabledQuery.data };
};
